import * as React from "react";
import { useCallback, useEffect, useMemo } from "react";
import { styled } from "@mui/material";
import { RosebudColorMap } from "../rosebud/rosebudColorMap";
import { NextStepPanel } from "../nextStep/NextStepPanel";
import { DiseaseCategoryType, QualifiedFeature } from "fe-common/graphql";
import { Suggestion } from "../hooks/useSuggestions";
import {
  PatientCaseChange,
  PatientCaseOp,
  usePatientCase,
} from "../usePatientCase";
import { mergeResponseToQF } from "../../dataInputTypes";
import { PatientCaseGrouping } from "../../../context/diagnosticAgent";
import { MobileView } from "../../../util/aKaunGeneralUtil";
import { Flipped, Flipper } from "react-flip-toolkit";
import { RoundedActionButton } from "../common/roundedActionButton";
import { ACTION, CATEGORY, evt } from "fe-common/analytics/analytics";
import { usePulse } from "../../../util/usePule";
import { KahunColorMap } from "fe-common/ui/assets/colorMap";
import { useDiagnosticNavigation } from "../useDiagnosticNavigation";
import { useScreenSize } from "../util/useScreenSize";
import { RosebudMobileBottomNavigationHeight } from "../common/rosebudButtomNavigationButton";
import { useChangePatientCase } from "../editPatient/useChangePatientCase";

//region [[ Styles ]]

const RecommendedPlanCategoryView = styled("div")(
  ({ theme }: { theme?: any }) => ({
    background: RosebudColorMap.White,
    [theme.breakpoints.up(MobileView)]: {
      "&.embedded-desktop": {
        boxShadow: "unset",
        margin: "0px",
        padding: "0px",
        marginBottom: "10px",
      },
      "&.improve-popup": {
        boxShadow: "unset",
        margin: "10px 6px",
        padding: "6px",
      },
    },
    display: "flex",
    flexDirection: "column",
    minHeight: "230px",
    minWidth: "200px",
    [theme.breakpoints.down(MobileView)]: {
      margin: 0,
      "&.embedded-mobile": {
        paddingRight: "0px",
        paddingLeft: "0px",
        backgroundColor: "white",
      },
      "&.improve-screen": {
        paddingRight: "16px",
        paddingLeft: "16px",
        backgroundColor: KahunColorMap.Secondary,
        // Replace margin with padding to get a green gap for the diagnose again button
        marginBottom: `-${RosebudMobileBottomNavigationHeight + 10}px`,
        paddingBottom: `${RosebudMobileBottomNavigationHeight}px`,
      },
    },
  }),
);

const RecommendedPlanTitleArea = styled("div")(({ theme }) => ({
  fontWeight: 500,
  fontSize: "16px",
  margin: "10px",
  display: "flex",
  flexDirection: "row",
  alignItems: "center",
  justifyContent: "flex-end", // Make the show more button align right
  flexWrap: "wrap-reverse",
  [theme.breakpoints.down(MobileView)]: {
    fontSize: "1.2em",
    flexWrap: "wrap",
  },
}));

export const NextStepItem = styled(NextStepPanel)(
  ({ theme }: { theme?: any }) => ({
    padding: "10px",
    "& .refine-panel": {
      minHeight: "auto",
      padding: "2px 16px",
      "& .relation-name": {
        fontSize: "15px",
        marginBottom: "10px",
      },
    },
    "& .refine-button": { padding: "2px 16px" },
    [theme.breakpoints.down(MobileView)]: {
      borderRadius: "8px",
      backgroundColor: "white",

      "&.embedded-mobile": {
        padding: "10px",
        marginBottom: "0px",
        boxShadow: "unset",
      },
      "&.improve-screen": {
        padding: "15px",
        marginBottom: "10px",
        boxShadow: "0px 1.17595px 4.70379px rgba(0, 0, 0, 0.08)",
      },
      "& .finding-title": {
        fontSize: "16px",
        margin: 0,
      },
    },
    [theme.breakpoints.up(MobileView)]: {
      "&.improve-popup": {
        padding: "17px",
        borderRadius: "9px",
        marginBottom: "10px",
        boxShadow: "0px 1.17595px 4.70379px rgba(0, 0, 0, 0.08)",
      },
      backgroundColor: "white",
      width: "100%",
      overflow: "hidden",
      "& .finding-icon": {
        display: "none",
      },
      "& .finding-title": {
        fontSize: "14px",
        margin: 0,
      },
    },
  }),
);

const ShowMore = styled(RoundedActionButton)({
  padding: "4px 7px",
  margin: "0 2px",
  textTransform: "none",
});

//endregion [[ Styles ]]

//region [[ Props ]]
interface RecommendedPlanCategoryProps {
  className?: string;
  category: DiseaseCategoryType;
  suggestions: Suggestion[];
  title?: string;
  maxItems?: number;
  showMore?: boolean;
  onShowMore?: (cat: DiseaseCategoryType) => void;
  onShowLess?: (cat: DiseaseCategoryType) => void;
  addChange: (change: PatientCaseChange) => void;
  isLoading: boolean;
  eventCategory: CATEGORY;
  isEmbedded: boolean;
}
//endregion [[ Props ]]

//region [[ Functions ]]
//endregion [[ Functions ]]

export const RecommendedPlanSection = ({
  suggestions,
  category,
  maxItems,
  showMore,
  onShowMore,
  onShowLess,
  isLoading,
  isEmbedded,
  ...props
}: RecommendedPlanCategoryProps) => {
  const { doChangesToPatientCase } = usePatientCase();
  const { setNextRouteNavigationDisabled } = useDiagnosticNavigation();
  const { isMobile } = useScreenSize();
  const { getCaseValue, getCaseValuePending } = useChangePatientCase();

  // Keep the diagnose button disabeld
  useEffect(() => {
    setNextRouteNavigationDisabled(true);
    return () => setNextRouteNavigationDisabled(false);
  }, [setNextRouteNavigationDisabled]);

  const totalSuggestionsCount = React.useMemo(
    () => suggestions.filter((sg) => sg.category === category).length,
    [suggestions, category],
  );

  const { pulse, withAnimation } = usePulse({
    disabled: !isLoading,
  });

  const filteredSuggestionsByCategory = React.useMemo(
    () =>
      suggestions
        .filter((sg) => sg.category === category)
        .slice(0, maxItems || 10),
    [suggestions, category, maxItems],
  );

  const sectionGrouping = useMemo(
    () =>
      category === DiseaseCategoryType.OutcomeSigns
        ? PatientCaseGrouping.CLINICAL_FEATURES
        : PatientCaseGrouping.WORKUP,
    [category],
  );

  const onAddFinding = useCallback(
    (suggestionFinding) => (resp: any) => {
      const finding = {
        //filter relations with no value
        ...suggestionFinding.finding,
        qualifiers: suggestionFinding.finding.qualifiers.filter((q) => q.value),
      };

      if (resp) {
        const level = resp?.level?.toUpperCase();
        const negate = resp.negate === false ? "YES" : "NO";
        const eventLabel =
          (level ? level : negate) +
          " " +
          suggestionFinding.finding.name +
          (finding.qualifiers.length > 0
            ? finding.qualifiers.map((q) => q.name).join(",")
            : "");

        evt({
          category: props.eventCategory,
          action: ACTION.ADD_FINDING_TO_PLAN,
          label: eventLabel,
          fb: true,
        });

        props.addChange({
          op: PatientCaseOp.replaceQFInSection,
          qf: mergeResponseToQF(finding, resp),
          section: sectionGrouping,
        });

        setNextRouteNavigationDisabled(false);
      }
    },
    [sectionGrouping, props, setNextRouteNavigationDisabled],
  );

  const onRemoveFinding = useCallback(
    (suggestionFinding: QualifiedFeature) => {
      props.addChange({
        op: PatientCaseOp.deleteFromSection,
        qf: suggestionFinding,
        featureId: suggestionFinding.id,
        section: sectionGrouping,
      });
    },
    [sectionGrouping, props],
  );

  const onSkipSuggestion = useCallback(
    (suggestionFinding) => () => {
      doChangesToPatientCase([
        {
          op: PatientCaseOp.addSkipToSection,
          qf: {
            ...suggestionFinding,
            skip: true,
          },
          section: sectionGrouping,
        },
      ]);
    },
    [doChangesToPatientCase, sectionGrouping],
  );

  const handleShowMoreLessClick = useCallback(() => {
    evt({
      category: props.eventCategory,
      action: showMore ? ACTION.PLAN_SHOW_LESS : ACTION.PLAN_SHOW_MORE,
      label: props.title,
    });

    if (showMore) {
      return onShowLess(category);
    } else {
      return onShowMore(category);
    }
  }, [
    category,
    onShowMore,
    onShowLess,
    showMore,
    props.title,
    props.eventCategory,
  ]);

  const flipKey = useMemo(
    () => filteredSuggestionsByCategory.map((s) => s?.finding?.id).join(","),
    [filteredSuggestionsByCategory],
  );

  const showMoreButton = useMemo(
    () =>
      onShowMore &&
      !isLoading &&
      (showMore ||
        totalSuggestionsCount > filteredSuggestionsByCategory.length),
    [
      onShowMore,
      isLoading,
      showMore,
      totalSuggestionsCount,
      filteredSuggestionsByCategory,
    ],
  );

  if (!filteredSuggestionsByCategory.length) return null;

  const getSelectionValue = (suggestion: Suggestion) => {
    const getPatientCaseValue = isMobile ? getCaseValue : getCaseValuePending;
    const value = getPatientCaseValue(
      suggestion.finding,
      PatientCaseGrouping.CLINICAL_FEATURES,
    );

    return value;
  };

  const className = isEmbedded
    ? isMobile
      ? "embedded-mobile"
      : "embedded-desktop"
    : isMobile
    ? "improve-screen"
    : "improve-popup";

  return (
    <RecommendedPlanCategoryView className={className}>
      <Flipper flipKey={flipKey}>
        {showMoreButton && (
          <RecommendedPlanTitleArea>
            <ShowMore
              className={`${isLoading ? pulse : ""} ${withAnimation}`}
              onClick={handleShowMoreLessClick}
              displayText={showMore ? "Show Less" : "Show More"}
            />
          </RecommendedPlanTitleArea>
        )}
        {!isLoading &&
          category === DiseaseCategoryType.OutcomeSigns &&
          filteredSuggestionsByCategory.map((suggestion, index) => (
            <Flipped
              flipId={suggestion.finding.id + index}
              key={suggestion.finding.id + index}
            >
              <NextStepItem
                className={className}
                suggestion={suggestion}
                response={getSelectionValue(suggestion)}
                skipSuggestion={onSkipSuggestion(suggestion)}
                onAddFinding={onAddFinding(suggestion)}
                onRemoveFinding={onRemoveFinding}
                showActions={false}
                showInlineReasoning={true}
              />
            </Flipped>
          ))}
        {/*  TODO: Generlize and unite to single component for both to remove duplicated code */}
      </Flipper>
    </RecommendedPlanCategoryView>
  );
};
